import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  countEntries,
  queryEntries,
  type LogCategory,
  type LogEntryRow,
  type LogLevel,
  type QueryOptions,
} from "@/services/logger/loggerStorage";

interface UseAppLogsOpts extends QueryOptions {
  /** Auto-refresh interval in ms when modal/panel is open. Default: 3000 */
  refetchInterval?: number;
  enabled?: boolean;
}

/**
 * Czyta logi z IndexedDB (Dexie). Auto-refresh w tle przy otwartym panelu.
 */
export function useAppLogs(opts: UseAppLogsOpts = {}) {
  const {
    refetchInterval = 3000,
    enabled = true,
    ...queryOpts
  } = opts;

  // serialize options for stable query key
  const queryKey = [
    "app-logs",
    queryOpts.category?.slice().sort().join(",") ?? "",
    queryOpts.level?.slice().sort().join(",") ?? "",
    queryOpts.search ?? "",
    queryOpts.since?.toISOString() ?? "",
    queryOpts.runId ?? "",
    queryOpts.limit ?? 0,
  ];

  const entriesQuery = useQuery({
    queryKey,
    queryFn: () => queryEntries(queryOpts),
    refetchInterval: enabled ? refetchInterval : false,
    enabled,
  });

  const totalQuery = useQuery({
    queryKey: ["app-logs-total"],
    queryFn: () => countEntries(),
    refetchInterval: enabled ? refetchInterval * 2 : false,
    enabled,
  });

  return {
    entries: entriesQuery.data ?? ([] as LogEntryRow[]),
    isLoading: entriesQuery.isLoading,
    isFetching: entriesQuery.isFetching,
    refetch: entriesQuery.refetch,
    total: totalQuery.data ?? 0,
    error: entriesQuery.error,
  };
}

/**
 * Helper: przelicz error count w ostatnich N godzinach (dla badge na FAB).
 */
export function useErrorCount(hours: number = 24, refetchMs: number = 10000) {
  const since = new Date(Date.now() - hours * 60 * 60 * 1000);
  return useQuery({
    queryKey: ["app-logs-error-count", hours],
    queryFn: () => countEntries({ level: ["ERROR"], since }),
    refetchInterval: refetchMs,
  });
}

/** Manual cache invalidation — useful gdy chcemy force refresh po użytkownik clear. */
export function useInvalidateAppLogs() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["app-logs"] });
    qc.invalidateQueries({ queryKey: ["app-logs-total"] });
    qc.invalidateQueries({ queryKey: ["app-logs-error-count"] });
  };
}

/**
 * Hook listening for runtime log level changes (Settings toggle).
 * Re-renders consuming components when localStorage 'app_log_level' changes.
 */
export function useLogLevel(): "production" | "debug" {
  const { data } = useQuery({
    queryKey: ["app-log-level"],
    queryFn: () => {
      if (typeof window === "undefined") return "production";
      const stored = localStorage.getItem("app_log_level");
      return stored === "debug" ? "debug" : "production";
    },
    refetchInterval: 2000,
  });
  return (data as "production" | "debug") || "production";
}

export type { LogCategory, LogLevel, LogEntryRow };
